import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AdaptivePracticeContext, AppContext } from '../../../context/context';
import { PlayFrame } from '../../adaptivePractice/frame/PlayFrame';
import { AssessmentInstructions, InstructionPoint } from '../../adaptivePractice/riseSession/instructions/AssessmentInstructions';
import { AssessmentQuestionStatus } from '../../adaptivePractice/interface/interface';
import { applyTheme, Theme } from '../../adaptivePractice/theme';
import { decryptData, getTimeSpent, isStaffOnly, toggleFullSceen } from '../../../utils/utils';
import { ls } from '../../../localStorage';
import apis from '../../../apis/api';
import { useEngineAccess } from '../builder/useEngineAccess';
import { agreedTimeLeft, blankAnswer, QuizAnswer, QuizQuestion, sittingToSend } from './sitting';

/**
 * THE INSTRUCTIONS, REOPENED - and nothing else on the screen moved.
 *
 * It holds the one piece of state and the player hands it a way in, exactly as the Save button does.
 * Were this the player's own state, opening the instructions would re-render the player, the frame
 * and the question inside it - and a question redrawn is a question reset: the engine keeps the
 * student's working in the DOM (what is typed, which box is focused, where the pieces have been
 * dragged), and React would put the drawn-from-config version back over all of it. Reading the
 * instructions must cost the student nothing.
 */
function ReopenedInstructions(props: {
  bind: { current: ((open: boolean) => void) | null },
  title: string,
  points: InstructionPoint[]
}) {
  const [open, setOpen] = useState(false);
  const { bind } = props;
  useEffect(() => {
    bind.current = setOpen;
    return () => { bind.current = null; };
  }, [bind]);
  if (!open) return null;
  return (
    <AssessmentInstructions title={props.title} points={props.points}
      startLabel='Back to the quiz' onClose={() => setOpen(false)} />
  );
}

/**
 * A student at a quiz.
 *
 * Played like a paper test, not like practice: every question is available at once, any of them can
 * be answered or come back to, nothing is marked right or wrong on the way, and one submit ends it. The questions are the teacher's frozen ones, replayed from their stored numbers, so the
 * class all answer the same paper.
 *
 * The screen is the Rise assessment frame - its question strip, clock, snapshots and submit popup -
 * with a different sitting underneath. What this file owns is the sitting: opening it, handing it
 * over as the student goes, and moving it into the database at the end.
 *
 * A quiz already finished opens as PRACTICE: the same questions, answered again for the sake of it,
 * nothing sent and nothing saved.
 */
export function AdaptiveQuizPlayer() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const QUERY = queryParams.get('query');
  const lang = queryParams.get('lang') || 'en';
  const state: any = useContext(AppContext);

  const launch = useMemo(() => {
    try {
      return QUERY ? decryptData(QUERY) : null;
    } catch {
      return null;
    }
  }, [QUERY]);

  const [currentUser, setCurrentUser] = useState<any>(null);
  const [quizId, setQuizId] = useState('');
  const [launchError, setLaunchError] = useState('');

  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [statuses, setStatuses] = useState<AssessmentQuestionStatus[]>([]);
  const [position, setPosition] = useState(0);
  const [response, setResponse] = useState<any>({ component: [] });
  const [snapShots, setSnapShots] = useState<{ [index: number]: string }>({});
  /**
   * What "Read question" should say for each question, kept beside its snapshot.
   *
   * A question the student has already seen is shown from its snapshot, and the drawn object is
   * replaced by an empty one - so `engineObj.sound` was gone and the button read nothing out. The
   * text is the engine's, taken when the question was drawn, so it is the same words they would
   * have heard the first time.
   */
  const [sounds, setSounds] = useState<{ [index: number]: any }>({});
  /**
   * The position whose LIVE render is on screen, as opposed to a snapshot of it.
   *
   * A snapshot is a cloned copy of the question's DOM, handed to the frame as innerHTML. Showing one
   * replaces the live React question wholesale, which repaints the whole area - and answering used
   * to do exactly that, the moment the answer was given, so a student saw their question appear to
   * reload under them.
   *
   * So a snapshot is never shown for the question being answered - only for one being returned to,
   * where it is the point: their own working, exactly as they left it.
   */
  const [liveAt, setLiveAt] = useState<number | null>(null);

  /** Seconds left in this sitting, or null when the teacher set no limit. */
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [showStartPopup, setShowStartPopup] = useState(true);
  /** How the Instructions button opens them again - a channel rather than state, so the question
   *  behind them is not redrawn. See ReopenedInstructions. */
  const openInstructions = useRef<((open: boolean) => void) | null>(null);
  /** Whether the sitting has begun. The instructions can be opened again once it has, so "the
   *  instructions are up" and "the quiz has not started" stopped being the same thing - and the
   *  clock must keep running while they read, or opening them would be a way of stopping it. */
  const [started, setStarted] = useState(false);
  /**
   * Which question the dialog is asking, if any.
   *
   * 'submit' - are you sure you want to hand this in.
   * 'leave'  - are you sure you want to leave. Only ever asked in PRACTICE: a graded quiz has no
   *            close button, because the way out of one is to hand it in.
   * The result of a submitted quiz is not one of these: it is `submitted`, and it reports rather than
   * asks.
   */
  const [ask, setAsk] = useState<'none' | 'submit' | 'leave'>('none');
  /** `submit` is declared below the fetch that needs it - a sitting with nothing left to play hands
   *  itself in - so the fetch reaches it through here. */
  const submitNothingLeft = useRef<(() => void) | null>(null);
  /** Already handed over and on the way out - the unmount must not send a second one. */
  const left = useRef(false);
  /**
   * The hand-over still in the air, if any.
   *
   * Ordinary hand-overs are deliberately not waited for - a student should never wait for the network
   * to answer the next question - but the one that leaves has to wait for them, or closing the frame
   * cancels whatever was mid-flight and the network log fills with failed calls that cost nothing but
   * look alarming.
   */
  const inFlight = useRef<Promise<void> | null>(null);
  /**
   * The handlers the leaving effect uses, held in a ref rather than depended upon.
   *
   * That effect must register ONCE and clean up ONCE, at unmount. Listing the handlers as
   * dependencies made React tear it down and set it up again every time one of them changed identity
   * - which happens the moment `fetch` sets the questions - and the teardown sends a hand-over. The
   * result was a hand-over while the student was still reading the instructions, and since the first
   * hand-over is what starts the server's clock, the reading was charged after all.
   */
  const leaving = useRef<{
    chargeTime: () => void;
    handOver: () => void;
    quizId: string;
    lang: string;
    numberLang: string;
  } | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<any>(null);
  /** A quiz that is already finished: replayed for practice, and nothing is sent. */
  const [practice, setPractice] = useState(false);
  /**
   * Opened to READ A STORED ATTEMPT rather than to play one - the report screens' way in.
   *
   * It is the same review this screen shows after a submit: the same marks, the same worked answers.
   * What differs is only where the attempt comes from and who asked for it:
   *
   *   a teacher   reading one student's answers, from classroom analytics. The payload names the
   *               student, and the attempt is read from the teacher's report endpoint.
   *   a student   reading their OWN answers from their quiz list - the same screen, the same
   *               payload without a student id, and their own endpoint. Not built yet; nothing here
   *               needs to change for it beyond choosing that call.
   *
   * Either way nothing about the quiz is touched: no sitting is opened, no seconds are charged and
   * nothing is submitted, which is why it is gated beside `practice` everywhere that writes.
   */
  const storedReviewRef = useRef(false);
  /** The same thing as the ref, for the render below - a ref would not re-render the frame. */
  const [storedReview, setStoredReview] = useState(false);
  /** Whether the teacher lets this class see their score. Off, and the result says how many were
   *  right without putting a percentage on it. */
  const [showScore, setShowScore] = useState(true);
  const [resumed, setResumed] = useState(false);
  /**
   * The whole quiz, and what earlier sittings already did with it.
   *
   * The questions on this screen are only the ones still to play, so after a reload the screen alone
   * cannot say how far the student has got. These come from the server, which holds the sitting.
   */
  const [totalQuestions, setTotalQuestions] = useState(0);
  /**
   * Looking back over a submitted quiz: which questions were right, which were wrong, and the worked
   * answer for each - the same review the assessment gives, and what the older quiz gave.
   */
  const [reviewing, setReviewing] = useState(false);
  /** Whose attempt a teacher is reading. Empty whenever a student is playing their own quiz. */
  const [studentId, setStudentId] = useState('');
  /**
   * WHICH of the answers to show, chosen by whoever opened the screen. It mirrors the `isCorrect` on
   * each answer, so there are three states and no new vocabulary:
   *
   *   true        only the ones they got right
   *   false       only the ones they got wrong
   *   undefined   all of them
   *
   * It exists because the report is reached by tapping a number - "Incorrect: 3" - and the teacher
   * who tapped it means those three, not the whole quiz. The caller says what it wants rather than
   * this screen guessing from who is looking.
   */
  const [isCorrectOnly, setIsCorrectOnly] = useState<boolean | undefined>(undefined);
  const results = useRef<{
    [index: number]: { isCorrect: boolean; points: number; answered: boolean; answer: any[] };
  }>({});

  const { engine, run: runOnEngine, claim, holds, alive } = useEngineAccess(Theme.Dawn);

  /** What has been done, by question index. The sitting, as this screen holds it. */
  const answers = useRef<{ [index: number]: QuizAnswer }>({});

  /** Which sitting the server filed this one as - everything handed over carries it. */
  const sessionNumber = useRef(1);
  /** When the question on screen was opened, for the seconds it has held the student. */
  /** A Date, not a timestamp: `getTimeSpent` reads `.getTime()` off whatever it is given. */
  const openedAt = useRef(new Date());
  const positionRef = useRef(0);
  const submittedRef = useRef(false);
  const practiceRef = useRef(false);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  const langObj = useMemo(() => {
    const country = String(
      currentUser?.classroom_country_code || currentUser?.country_code || 'in',
    ).toLowerCase();
    return { lang: `${lang}_${country}`, number_lang: 'en' };
  }, [lang, currentUser]);

  useEffect(() => {
    applyTheme(Theme.Dawn);
  }, []);

  /** The payload teacher-web's student counterpart hands over: who is playing, and which quiz. */
  useEffect(() => {
    try {
      if (!launch) throw new Error('Missing or unreadable query');
      const user = launch.currentUser;
      if (!user?.token || !user?.dbToken) throw new Error('Invalid session');
      const quiz = String(launch.quiz_id || '');
      if (!quiz) throw new Error('Missing quiz');
      ls.setToken(user.token);
      ls.setDBToken(user.dbToken);
      setCurrentUser(user);
      setQuizId(quiz);
      /**
       * WHO is here decides what this screen may do; the payload only says which attempt to show.
       *
       * The role comes off `currentUser`, as the other play screens read it, through `isStaffOnly`
       * in utils - one definition of the question, for any screen that needs to ask it.
       *
       *   anyone but a student   can only READ an attempt, and only a named student's. There is no
       *                          playing path for them: /student/fetch is student-only and would
       *                          answer 403, leaving a blank frame instead of a reason.
       *   a student              plays, unless the payload asks for their own answers (`review`).
       *                          A `user_id` from a student is ignored - their own is the only
       *                          attempt they may read, and the server takes that from the token.
       *
       * Fail-safe on purpose: only an explicitly teacher-ish role takes the first branch, so a
       * payload that somehow carries no role still lets a student play.
       */
      if (typeof launch.isCorrect === 'boolean') setIsCorrectOnly(launch.isCorrect);

      if (isStaffOnly(user)) {
        // Whose attempt to show. Only staff may name a student; a student reads their own, and the
        // server takes that id from their token.
        const student = String(launch.user_id || '');
        // Without one there is nothing to show and no playing path open to them, so the frame closes.
        if (!student) throw new Error('Missing student for a review');
        setStudentId(student);
        storedReviewRef.current = true;
        setStoredReview(true);
      } else if (launch.review === true) {
        // A student reading their OWN answers, which is what their report asks for. No id travels
        // with it - the server reads theirs from the token - so this is the only thing that tells a
        // report apart from a quiz opened to be played, which sends neither.
        storedReviewRef.current = true;
        setStoredReview(true);
      }
    } catch (error) {
      console.log('quiz player: could not read launch payload', error);
      setLaunchError('This quiz could not be opened. Please close and try again.');
      setLoading(false);
      // And ask the host to take the frame down. On its own the message sat in an iframe with no way
      // out of it: this screen's close button belongs to the play frame, which never renders when the
      // launch itself failed.
      state?.messageForParent?.('close');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * How long the question on screen has held the student, added to whatever it held them before.
   *
   * Called whenever the screen leaves a question - answered, moved away from, or the whole quiz
   * closed - so the total is the sum of every visit rather than the last one.
   */
  const chargeTime = useCallback(() => {
    const index = questions[positionRef.current]?.index;
    if (index === undefined) return;
    const seconds = getTimeSpent(openedAt.current);
    openedAt.current = new Date();
    const held = answers.current[index];
    if (held) {
      held.time_spent = held.time_spent + seconds;
      // Returned to and sat with: the time has grown, so this is ours to report now even if the
      // answer itself came from an earlier sitting.
      delete held.fromServer;
      return;
    }
    // Seen and left: it counts as used up, or it would be offered again in the next sitting.
    answers.current[index] = blankAnswer(index, seconds);
  }, [questions]);

  /**
   * Hand the sitting over.
   *
   * Everything held goes each time, not a delta: the server keeps the later answer and the larger
   * time, so a hand-over that is lost or overtaken costs nothing. With no answers at all it is the
   * heartbeat - it records nothing, and tells the server the student is still here.
   */
  const handOver = useCallback(
    async (mode: 'progress' | 'beat' = 'progress') => {
      if (storedReviewRef.current || practiceRef.current || submittedRef.current || !quizId)
        return;
      const body = {
        quiz_id: quizId,
        session_number: sessionNumber.current,
        lang: langObj.lang,
        number_lang: langObj.number_lang,
        // EVERYTHING held, not just what this sitting did. It has to be: a question merely seen and
        // left is recorded here as a blank by chargeTime, and that blank is what tells the server the
        // question is used up and must not be offered again. Filtering this down to the questions
        // this sitting answered dropped exactly those blanks, and the question came back on reopen.
        //
        // Sending the lot is safe because the SERVER protects what it has already decided: for an
        // answer whose content has not changed it keeps its own isCorrect, status and session_number,
        // so the `false` that seeded answers carry - fetch withholds verdicts mid-attempt - cannot
        // overwrite them. One place to get right, on the side that owns the truth.
        answers: mode === 'beat' ? [] : sittingToSend(answers.current),
      };
      try {
        const res: any = await apis._saveAdaptiveQuizProgress(body);
        const held = res?.data?.collection;
        if (held?.time_left !== undefined) {
          setTimeLeft((local) => agreedTimeLeft(local, held.time_left));
        }
      } catch (error) {
        // Nothing is lost by a failed hand-over: the next one carries the same sitting again.
        console.log('quiz player: could not hand over the sitting', error);
      }
    },
    [langObj.lang, langObj.number_lang, quizId],
  );

  /**
   * Read a finished attempt and show it marked: the report's way in.
   *
   * One call brings the quiz's frozen questions AND what the student answered, so the whole review
   * is built from it; no sitting is opened and no clock runs. `isLive` says whether the screen is
   * still the one that asked - a reply that arrives after the student has closed it is dropped.
   *
   * Which call answers is the only thing that differs between a teacher reading someone's answers
   * and a student reading their own. Both reply in the same shape, so everything after it is the
   * same work.
   */
  const loadStoredAttempt = useCallback(
    async (
      quiz: string,
      student: string,
      isCorrect: boolean | undefined,
      isLive: () => boolean,
    ) => {
      const res: any = student
        ? await apis._fetchAdaptiveQuizAttemptForTeacher(quiz, student)
        : await apis._fetchMyAdaptiveQuizAttempt(quiz);
      const attempt = res?.data?.collection;
      if (!attempt) throw new Error(res?.data?.message || 'Attempt not found');
      if (!isLive()) return;
      /**
       * Only the answers the caller asked for.
       *
       * Filtered here rather than asked of the server, because the one call already carries the whole
       * attempt and each question says whether it was right - so the screen can show a different part
       * of the same reply without another request.
       *
       * A question that was never reached counts as neither: it is not a wrong answer, it is an
       * absent one, so it belongs in neither list.
       *
       * Each question keeps its own `index`, which is what the marks are keyed by, so a shortened
       * list still reads the right verdict for every question in it.
       */
      const wanted = (attempt.questions || []).filter((q: any) => {
        if (isCorrect === undefined) return true;
        if (isCorrect) return q.isCorrect === true;
        return q.attempted === true && q.isCorrect !== true;
      });
      // Nothing matched - the attempt changed under the teacher, or the quiz was re-answered. Showing
      // the whole attempt says more than an empty frame would.
      const chosen = wanted.length > 0 ? wanted : attempt.questions || [];
      const list: QuizQuestion[] = chosen.map(
        (q: any, i: number) => ({
          // The report names this `questionType` - what the question IS, skill or HOTS - beside
          // the `attemptType` of the stored answer. This screen only needs the first: it is what
          // decides which loader draws the question.
          type: q.questionType,
          question_id: q.question_id,
          lo_id: q.lo_id ? String(q.lo_id) : undefined,
          review_obj: q.review_obj,
          points: Number(q.points) || 1,
          index: Number.isFinite(q.index) ? Number(q.index) : i,
        }),
      );
      // The marks, from the stored rows rather than from this screen - which has played nothing.
      results.current = {};
      chosen.forEach((q: any, i: number) => {
        const index = Number.isFinite(q.index) ? Number(q.index) : i;
        results.current[index] = {
          isCorrect: q.isCorrect === true,
          points: Number(q.earned) || 0,
          answered: q.attempted === true,
          answer: Array.isArray(q.answer) ? q.answer : [],
        };
      });
      setShowScore(true);
      // The number of questions ON SCREEN, which with a filter is fewer than the quiz holds.
      setTotalQuestions(list.length);
      setQuestions(list);
      // Every question is answered as far as this screen is concerned: the strip shows right and
      // wrong rather than progress, and there is no clock and no instructions to get past.
      setStatuses(list.map(() => AssessmentQuestionStatus.answered));
      setTimeLeft(null);
      setShowStartPopup(false);
      setReviewing(true);
    },
    [],
  );

  /**
   * Open the quiz to be PLAYED, which starts a sitting: the reply carries only the questions still
   * to play, the seconds left and the session number to stamp this sitting with.
   *
   * It also takes over whatever the server is holding of an earlier sitting - the answers and the
   * seconds - so the tallies, the submit dialog's counts and every hand-over work from one place
   * whether this is the first sitting or the fifth. A quiz that is already finished comes back
   * `completed`, and this screen replays it as practice instead, writing nothing.
   */
  const openSitting = useCallback(
    async (quiz: string, isLive: () => boolean) => {
      const res: any = await apis._fetchAdaptiveQuizForStudent(quiz);
      const quizData = res?.data?.collection;
      if (!quizData) throw new Error(res?.data?.message || 'Quiz not found');
      if (!isLive()) return;
      const list: QuizQuestion[] = (quizData.questions || []).map((q: any, i: number) => ({
        type: q.type,
        question_id: q.question_id,
        lo_id: q.lo_id ? String(q.lo_id) : undefined,
        review_obj: q.review_obj,
        points: Number(q.points) || 1,
        index: Number.isFinite(q.index) ? Number(q.index) : i,
      }));
      sessionNumber.current = Number(quizData.session_number) || 1;
      practiceRef.current = quizData.completed === true;
      setPractice(quizData.completed === true);
      setResumed(quizData.resumed === true);
      setShowScore(quizData.isShowScore !== false);
      setTotalQuestions(Number(quizData.total_questions) || list.length);
      // Take over the attempt the server is holding. Everything that follows - the tallies, the
      // submit dialog's counts, what a hand-over carries - then works from one place, whether this
      // is the first sitting or the fifth. The seconds come across too, so nothing is lost by
      // handing them back.
      answers.current = {};
      // NOT in practice. `attempted` for a finished quiz is the GRADED attempt, read back from the
      // stored rows - every question of it - which is what a review is built from. A practice replay
      // seeded with it would open with every question already answered, so its dialog would count
      // the old attempt's answers and its score would be the old attempt's score. A replay starts
      // from nothing, as a replay should.
      if (quizData.completed !== true) {
        (quizData.attempted || []).forEach((a: any) => {
          const index = Number(a.index);
          if (!Number.isFinite(index)) return;
          answers.current[index] = {
            index,
            status: a.status === 'answered' ? 'answered' : 'unanswered',
            answer: Array.isArray(a.answer) ? a.answer : [],
            // Whether it was right is not sent mid-attempt, and this screen has no use for it: the
            // submit decides the marks, and the review reads them from there.
            isCorrect: a.isCorrect === true,
            time_spent: Number(a.time_spent) || 1,
            // The server's, not ours: left out of a hand-over until the student does something
            // to it. See fromServer on QuizAnswer.
            fromServer: true,
          };
        });
      }
      // Every question has been seen already, so there is nothing to play and nothing to explain:
      // straight to the question this screen exists to ask, which is whether to hand it in. The
      // clock is still running until they do, which is exactly why it is asked at once.
      if (quizData.completed !== true && list.length === 0) {
        setShowStartPopup(false);
        // Nothing left to answer, so there is nothing to ask about: hand it in and show the result.
        // A dialog here would offer "Keep Answering" with no questions to answer.
        void submitNothingLeft.current?.();
      }
      setQuestions(list);
      setStatuses(list.map(() => AssessmentQuestionStatus.notVisited));
      setTimeLeft(
        quizData.completed === true ||
          quizData.time_left === null ||
          quizData.time_left === undefined
          ? null
          : Number(quizData.time_left),
      );
    },
    [],
  );

  /**
   * Open the quiz, once the launch payload has said which quiz and who for.
   *
   * Two ways to open one, and they share nothing but the loader and the error: an attempt that is
   * finished is READ, marked right and wrong, while a quiz still to play has a sitting opened for
   * it. `storedReviewRef` holds which, decided from the role and the payload above.
   *
   * `cancelled` guards every write that follows an await: the student can close the screen, or be
   * moved to another quiz, while a reply is still in the air.
   */
  useEffect(() => {
    if (!currentUser || !quizId) return;
    let cancelled = false;
    const isLive = () => !cancelled;
    (async () => {
      setLoading(true);
      try {
        if (storedReviewRef.current)
          await loadStoredAttempt(quizId, studentId, isCorrectOnly, isLive);
        else await openSitting(quizId, isLive);
      } catch (error) {
        console.log('quiz player: could not open the quiz', error);
        if (isLive()) setNotice('This quiz could not be opened. Please close and try again.');
      } finally {
        if (isLive()) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, quizId, studentId, isCorrectOnly, loadStoredAttempt, openSitting]);

  /**
   * A question the student has now opened counts as SEEN, whether or not it could be drawn.
   *
   * Seen, not answered: it is exactly the state of a question they looked at and moved past. A
   * question that cannot be drawn is still a question they were shown and cannot answer, and the
   * submit already treats it that way - it sends an empty answer and the server writes the row.
   *
   * It matters because of what is waiting on it: Submit appears only once NO question is still
   * notVisited. Marked on the draw alone, a question with a broken review_obj stayed notVisited for
   * good, so the student reached the last question with no way to hand the quiz in.
   */
  const markSeen = useCallback(() => {
    setStatuses((current) =>
      current.map((status, i) =>
        i === positionRef.current && status === AssessmentQuestionStatus.notVisited
          ? AssessmentQuestionStatus.unanswered
          : status,
      ),
    );
  }, []);

  /** Draw the question at this position, from the numbers the teacher froze. */
  const showQuestion = useCallback(async () => {
    const question = questions[positionRef.current];
    if (!question) return;
    const ticket = claim();
    setLoading(true);
    try {
      const drawn: any = await runOnEngine(async () => {
        if (question.type === 'HOTS') {
          engine.content.setTheme(Theme.Dawn);
          const loaded: any = engine.content.loadHotsQuestionById(question.question_id);
          if (!loaded) return null;
          return engine.factory.displayQuestion(
            engine.content,
            engine.content.createContentTemplateObject(loaded.id, 'REACT_WEB'),
            langObj,
            question.review_obj,
          );
        }
        await engine.content.loadSkillBySkillId(String(question.lo_id), Theme.Dawn, langObj.lang);
        return engine.factory.displayQuestion(
          engine.content,
          engine.content.createContentTemplateObject(question.question_id, 'REACT_WEB'),
          langObj,
          question.review_obj,
        );
      });
      if (!holds(ticket) || !alive.current) return;
      if (!drawn?.component) throw new Error('Question could not be drawn');
      // This one is fine, so whatever the last one left on screen goes. The frame holds a notice
      // until it is cleared, so a question that failed to draw went on apologising over every
      // question after it - including the one it had just told them to try.
      setNotice('');
      setResponse(drawn);
      setSounds((current) => ({ ...current, [positionRef.current]: drawn.sound }));
      setLiveAt(positionRef.current);
      openedAt.current = new Date();
      markSeen();
    } catch (error) {
      console.log('quiz player: could not draw a question', error);
      if (holds(ticket)) {
        setNotice('This question could not be shown. Try the next one.');
        // Seen all the same - see markSeen. Without this the quiz could never be handed in.
        markSeen();
      }
    } finally {
      if (holds(ticket)) setLoading(false);
    }
  }, [alive, claim, engine, holds, langObj, markSeen, questions, runOnEngine]);

  /** The first question, once the student has read the instructions. */
  useEffect(() => {
    if (questions.length === 0 || showStartPopup) return;
    // While PLAYING, a question already answered comes back from its snapshot, so there is nothing
    // to draw: going to the engine first would show a loader and then be thrown away by the
    // snapshot anyway. That double work is what made moving back to an answered question feel like
    // a fresh load.
    //
    // A REVIEW is the opposite and must always draw. The snapshots are still in state - nothing
    // clears them - but the review does not render them: it renders the question itself, marked
    // right or wrong with its worked answer, which only a fresh draw produces. Skipping it here
    // left the previous question on screen beside the new one's verdict, so the marks were right
    // and the question beneath them was the one before it.
    if (!reviewing && snapShots[position] !== undefined) return;
    void showQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, showStartPopup, position, reviewing]);

  /** The heartbeat: what lets the clock be charged when a device dies without saying goodbye. */
  useEffect(() => {
    if (storedReviewRef.current || practice || submitted || showStartPopup) return;
    const beat = window.setInterval(() => {
      // Not while the screen is on its way out: that beat would be cancelled by the unmount and show
      // up as a failed call for no reason.
      if (left.current) return;
      chargeTime();
      inFlight.current = handOver('progress');
    }, 60000);
    return () => window.clearInterval(beat);
  }, [chargeTime, handOver, practice, submitted, showStartPopup]);

  const goTo = (next: number) => {
    if (next === positionRef.current) return;
    chargeTime();
    inFlight.current = handOver();
    /**
     * Leaving a question gives up the live render with it.
     *
     * `liveAt` means "the question drawn on screen is this one". Moving away makes that untrue, and
     * leaving it set had a precise consequence: a question answered and then returned to was shown
     * from the LIVE branch again rather than from its snapshot. The frame had unmounted that subtree
     * when it swapped to the next question's snapshot, so coming back mounted a fresh one - and the
     * parts that take an answer start clean. The question looked unanswered and answerable, with the
     * student's own working nowhere to be seen. It righted itself only after visiting a question
     * with no snapshot, because that forced a draw and moved `liveAt` along.
     *
     * The response goes too. Until the snapshot or the new draw arrives the live branch would
     * otherwise render whatever was last drawn, which is the question they have just left.
     */
    // A question already answered comes back from its snapshot without being drawn, so the draw
    // above is not enough on its own: moving is what dismisses the message either way.
    setNotice('');
    setLiveAt(null);
    setResponse({ component: [] });
    setPosition(next);
  };

  const onAnswer = (answerObj: { givenAnswer: any; actualAnswer: any; isCorrectAns: Boolean }) => {
    const question = questions[positionRef.current];
    if (!question) return;
    const held = answers.current[question.index];
    const seconds = getTimeSpent(openedAt.current);
    openedAt.current = new Date();
    answers.current[question.index] = {
      index: question.index,
      status: 'answered',
      answer: Array.isArray(answerObj.givenAnswer)
        ? answerObj.givenAnswer
        : [answerObj.givenAnswer],
      isCorrect: answerObj.isCorrectAns === true,
      time_spent: (held?.time_spent || 0) + seconds,
    };
    setStatuses((current) =>
      current.map((status, i) =>
        i === positionRef.current ? AssessmentQuestionStatus.answered : status,
      ),
    );
    inFlight.current = handOver();
    // Deliberately NOT moving on. The student presses Next when they are ready, which is how a paper
    // test reads - and it keeps the answer on screen, which is what the frame snapshots. Advancing
    // here would file that snapshot against the question they had been moved to.
  };

  /** What a practice replay scores, worked out here because nothing is sent. */
  const practiceResult = () => {
    // The marks, worked out here because nothing was sent. They go in the same place a submitted
    // quiz's do, so the review reads a practice run exactly as it reads a graded one.
    results.current = {};
    questions.forEach((q) => {
      const given = answers.current[q.index];
      results.current[q.index] = {
        isCorrect: given?.isCorrect === true,
        points: given?.isCorrect === true ? q.points : 0,
        answered: (given?.answer || []).length > 0,
        answer: given?.answer || [],
      };
    });
    const correct = Object.values(answers.current).filter((a) => a.isCorrect).length;
    const coins = questions
      .filter((q) => answers.current[q.index]?.isCorrect)
      .reduce((sum, q) => sum + q.points, 0);
    const total = questions.reduce((sum, q) => sum + q.points, 0);
    return {
      correct,
      total: questions.length,
      coins,
      percentage: total > 0 ? Math.round((coins / total) * 100) : 0,
      practice: true,
    };
  };

  const submit = useCallback(
    async (isTimeout = false) => {
      // An attempt opened to be READ can never be handed in - nothing on this screen is
      // theirs to submit. Said here rather than only at the callers, so no control that is ever
      // wired to this can submit for a student by accident.
      if (storedReviewRef.current) return;
      if (submittedRef.current) return;
      submittedRef.current = true;
      chargeTime();
      setAsk('none');
      if (practiceRef.current) {
        setResult(practiceResult());
        setSubmitted(true);
        return;
      }
      setLoading(true);
      try {
        const res: any = await apis._submitAdaptiveQuiz({
          quiz_id: quizId,
          session_number: sessionNumber.current,
          lang: langObj.lang,
          number_lang: langObj.number_lang,
          isTimeout,
          // EVERYTHING here, unlike a hand-over: this is the last word, so if the server's copy of
          // the sitting were ever short of something, the answers are better recovered from here
          // than lost. The verdicts are safe either way - the server keeps its own for an answer
          // that has not changed - so the `false` the server's own answers carry cannot overwrite
          // them. The marker is stripped: it is ours, not part of the request's shape.
          answers: Object.values(answers.current).map(({ fromServer, ...answer }) => answer),
        });
        const done = res?.data?.collection;
        setResult(done || null);
        results.current = {};
        (done?.results || []).forEach((r: any) => {
          results.current[Number(r.index)] = {
            isCorrect: r.isCorrect === true,
            points: Number(r.points) || 0,
            answered: r.answered === true,
            answer: Array.isArray(r.answer) ? r.answer : [],
          };
        });
        setSubmitted(true);
        state?.messageForParent?.('adaptiveQuizCompleted', {
          quiz_id: quizId,
          percentage: done?.percentage ?? 0,
          coins: done?.coins ?? 0,
          // `answered`, NOT `correct` - the response carries both and they are different numbers.
          // student-web adds this straight onto the day's question count and the daily-goal
          // arithmetic, so sending the correct-answer count made a student who answered ten and got
          // six right count as six questions answered, and reach their goal four questions late.
          // The server counted all ten, so the dashboard and the database disagreed.
          answered: done?.answered ?? 0,
          // The SERVER's total, for the same reason as `answered` above: it is summed from the
          // attempt the service holds, so it covers sittings this screen never saw and the
          // wall-clock the server charges, neither of which our own sum can know about. It is also
          // the exact figure the day's summary was written with, so the dashboard and the database
          // agree. Our sum is kept as a fallback, for a client running against a service that has
          // not been deployed with `time_spent` yet.
          time_spent:
            done?.time_spent ??
            Object.values(answers.current).reduce((sum, a) => sum + a.time_spent, 0),
        });
      } catch (error) {
        console.log('quiz player: could not submit the quiz', error);
        // Let them try again rather than losing the sitting: it is still held on the server.
        submittedRef.current = false;
        setNotice('Your answers could not be sent. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chargeTime, langObj.lang, langObj.number_lang, questions, quizId, state],
  );

  useEffect(() => {
    submitNothingLeft.current = () => void submit(false);
  }, [submit]);

  useEffect(() => {
    leaving.current = {
      chargeTime,
      handOver: () => {
        inFlight.current = handOver();
      },
      quizId,
      lang: langObj.lang,
      numberLang: langObj.number_lang,
    };
  }, [chargeTime, handOver, langObj.lang, langObj.number_lang, quizId]);

  /**
   * The clock was already gone when they opened it.
   *
   * Not the ordinary case - the frame runs the countdown and ends the quiz itself - but the frame only
   * mounts a clock while there is time on it. A student who left a timed quiz and came back after it
   * ran out gets `time_left: 0`, no countdown, and would otherwise sit in front of 00:00:00 with
   * nothing happening. `timeLeft` starts as null, so this cannot fire before the quiz has loaded.
   */
  useEffect(() => {
    if (timeLeft === 0 && !storedReviewRef.current && !practice && !submitted)
      void submit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, practice, submitted]);

  /**
   * Look back over the quiz just submitted.
   *
   * A submitted quiz comes back WHOLE from the server - every question with its frozen numbers and
   * its points - so the same screen replays it with each one marked right or wrong and its worked
   * answer open. Nothing is sent from here: the attempt is already filed, and every hand-over stops
   * itself once the quiz has been submitted.
   */
  const startReview = useCallback(async () => {
    // A practice run already HAS the whole quiz on screen - that is what a replay is - and its marks
    // were worked out locally, so there is nothing to ask the server for.
    if (practiceRef.current) {
      setTimeLeft(null);
      // Let go of whatever was last played before the review draws its first question - see the
      // note on the strip below. Entering the review is a move like any other.
      setNotice('');
      setLiveAt(null);
      setResponse({ component: [] });
      setPosition(0);
      setAsk('none');
      setReviewing(true);
      return;
    }
    setLoading(true);
    try {
      const res: any = await apis._fetchAdaptiveQuizForStudent(quizId);
      const quiz = res?.data?.collection;
      const list: QuizQuestion[] = (quiz?.questions || []).map((q: any, i: number) => ({
        type: q.type,
        question_id: q.question_id,
        lo_id: q.lo_id ? String(q.lo_id) : undefined,
        review_obj: q.review_obj,
        points: Number(q.points) || 1,
        index: Number.isFinite(q.index) ? Number(q.index) : i,
      }));
      if (list.length === 0) {
        state?.messageForParent?.('close');
        return;
      }
      // No clock in a review, and the strip's statuses give way to right and wrong.
      setTimeLeft(null);
      setQuestions(list);
      setStatuses(list.map(() => AssessmentQuestionStatus.answered));
      setNotice('');
      setLiveAt(null);
      setResponse({ component: [] });
      setPosition(0);
      setAsk('none');
      setReviewing(true);
    } catch (error) {
      console.log('quiz player: could not open the review', error);
      state?.messageForParent?.('close');
    } finally {
      setLoading(false);
    }
  }, [quizId, state]);

  /**
   * NO prompt before the page goes - deliberately.
   *
   * There used to be a `beforeunload` here, so that reloading or closing the tab asked first. It was
   * removed because the only thing it could ever say was wrong. Every browser ignores the message a
   * page supplies and shows its own, which is some variant of "changes you made may not be saved" -
   * and in this quiz they ARE saved: every answer is handed over as the student goes, there is a
   * heartbeat behind it, and `pagehide` below sends the last of it with `keepalive`. Telling a
   * student their work might be lost, when it is already on the server, is worse than saying nothing.
   *
   * The thing that genuinely does not come back - a question once seen is not offered again - is said
   * in the instructions before they start, in our own words, where it can actually be read.
   */

  /**
   * Leaving without submitting.
   *
   * Two different exits, and they need two different sends.
   *
   * Moving away inside the app runs the cleanup below, where an ordinary call is fine. A tab or a
   * phone being CLOSED cancels any request in flight, so that one goes out through
   * `_handOverAdaptiveQuizOnExit`, which sends it with `keepalive` - see the note on it in api.ts for
   * why that transport and not axios or sendBeacon.
   */
  useEffect(() => {
    const onPageHide = () => {
      // Everything through the ref: this listener is registered once, at mount, when the launch
      // payload has not even been read - the quiz id would still be empty in its closure.
      const now = leaving.current;
      if (storedReviewRef.current || practiceRef.current || submittedRef.current || !now?.quizId)
        return;
      now.chargeTime();
      apis._handOverAdaptiveQuizOnExit({
        quiz_id: now.quizId,
        session_number: sessionNumber.current,
        lang: now.lang,
        number_lang: now.numberLang,
        // As the ordinary hand-over does - including the blanks that mark a question seen and left.
        answers: sittingToSend(answers.current),
      });
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      // Left inside the app: the ordinary call, which the page sticks around long enough to finish.
      // Nothing to do if the quiz was submitted or is a practice replay.
      //
      // The lint rule warns that these refs may have changed since the effect ran. They will have,
      // and that is the point: what matters is whether the quiz is finished NOW, at the moment of
      // leaving - a value copied in when the screen mounted would always say no.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (storedReviewRef.current || practiceRef.current || submittedRef.current || left.current)
        return;
      leaving.current?.chargeTime();
      leaving.current?.handOver();
    };
    // ONCE. See the note on `leaving` for what listing the handlers here used to cost.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * What this quiz tells the student before they start.
   *
   * Its own facts, not the STAAR test's: how long they actually have (or that there is no limit),
   * how many questions there actually are, and that a question is answered or it is not - there is
   * no skipping here, so nothing says there is.
   */
  const instructionPoints = () => {
    const minutes = timeLeft === null ? 0 : Math.max(1, Math.round(timeLeft / 60));
    const plural = minutes === 1 ? '' : 's';
    return [
      // What it IS comes first. The clock is the second thing a student wants to know, not the first
      // thing they are met with.
      ...(practice
        ? [
            {
              point:
                'You have already finished this quiz. This time is for practice, and nothing is saved.',
            },
          ]
        : []),
      {
        /**
         * The whole quiz AND what is left of it, when those differ.
         *
         * Only the unanswered questions are served on a return, so saying "this quiz has 3 questions"
         * would be untrue - the quiz has ten, and three of them are still theirs to answer. The count
         * on their card, and the numbers along the top, are about what is left; this is what ties the
         * two figures together.
         */
        point:
          totalQuestions > questions.length
            ? `This quiz has ${totalQuestions} question${totalQuestions === 1 ? '' : 's'}, and ${
                questions.length
              } of them ${questions.length === 1 ? 'is' : 'are'} left for you to answer.`
            : `This quiz has ${questions.length} question${questions.length === 1 ? '' : 's'}.`,
      },
      {
        // "Left" only makes sense to somebody coming BACK. On a first sitting the whole limit is
        // theirs, and saying what is left of it would read as though some had already gone.
        point:
          timeLeft === null
            ? 'There is no time limit. Take as long as you need.'
            : resumed
              ? `You have about ${minutes} minute${plural} left. The quiz is submitted for you when the time runs out.`
              : `You have ${minutes} minute${plural} for this quiz. It is submitted for you when the time runs out.`,
      },
      {
        point: 'Move between questions with Previous and Next, or by the numbers at the top.',
        subPoints: [
          { subPoint: 'The question you are on is highlighted' },
          { subPoint: 'Green = answered' },
        ],
      },
      { point: 'You can change an answer until you submit.' },
      ...(practice
        ? []
        : [
            {
              // The one thing a student has to DO for an answer to count. The quiz holds what they
              // have done to a question until they press Save, so this says it in those words
              // rather than promising it happens by itself.
              point:
                'Your answers will be saved only when you click Save for each question. Click Submit to finish the quiz.',
            },
            // The rule a student can be caught out by, so it is said BEFORE they start and not only
            // to someone who has already lost a question to it.
            {
              point:
                'If you close the quiz and open it again, you carry on with the questions that are left. A question you have already seen is not asked again, even if you did not answer it - so answer each one before you move on.',
            },
          ]),
    ];
  };

  /** Answered anywhere in the quiz. The sitting held here starts from the server's own copy of it, so
   *  this covers the questions dealt with before this screen ever opened. */
  const answeredAll = () =>
    Object.values(answers.current).filter(
      (a) => a.status === 'answered' && (a.answer || []).length > 0,
    ).length;

  const popUpInfo = () => {
    const answered = answeredAll();
    const heading = showStartPopup
      ? practice
        ? 'This quiz is finished. Play it again for practice - nothing you do now is saved.'
        : 'You are about to start a quiz. Answer the questions in any order, then submit.'
      : ask === 'leave'
        ? 'Leave this practice?'
        : !submitted
          ? practice
            ? 'Finish this practice?'
            : 'Are you sure you want to submit?'
          : practice
            ? 'Practice finished - nothing was saved.'
            : // The score is not said here: it has its own line below, beside the coins, which is
              // where a student looks for it. A quiz with the score turned off shows neither.
              //
              // Whether the clock ended it comes from the SERVER, which decides that from its own
              // timing - a student whose quiz was taken out of their hands should be told so
              // plainly rather than left wondering why it went.
              result?.isTimeout === true
              ? "Time's up! Your quiz has been submitted with your answers so far."
              : 'Your quiz has been submitted.';
    return {
      isShowStartPopup: showStartPopup,
      isAssessment: true,
      assessment: {
        answered,
        unanswered: Math.max(0, (totalQuestions || questions.length) - answered),
        // NOT in a practice replay. Nothing was credited there, so "you have earned 21 coins" would
        // be a plain untruth - the score below is what a practice run has to show for itself.
        coinsEarned: submitted && showScore && !practice ? (result?.coins ?? 0) : 0,
        // The score, alongside the coins - and only where there is one to give. A teacher who turned
        // the score off gets neither; a practice replay scores itself and says so in its heading.
        ...(submitted &&
        showScore &&
        result?.percentage !== undefined &&
        result?.percentage !== null
          ? { percentage: result.percentage }
          : {}),
        total: totalQuestions || questions.length,
      },
      /* "Close", not "Okay", once the quiz is in: it stands beside "Review Answers", and between two
         buttons the one that leaves has to say that it leaves. "Okay" read as agreeing with the
         result rather than as the way out of it. */
      stopBtnText:
        ask === 'leave' ? 'Leave' : submitted ? 'Close' : practice ? 'Finish' : 'Submit',
      /**
       * The dialog's SECOND button only exists when this is set, and it says different things at
       * different moments: while playing it is the way back out of a question we are asking, and after
       * submitting it is the way into the review. A practice replay has no review - nothing was
       * marked, because nothing was sent - so there it is left off and Close simply closes.
       */
      // The second button: a way back while playing, and the way into the review once finished. A
      // practice run gets it too - it scores itself, so there is just as much to look over.
      isCloseFromClick: !submitted || !reviewing,
      continueBtnText:
        ask === 'leave' ? 'Keep Playing' : submitted ? 'Review Answers' : 'Keep Answering',
      heading,
      /**
       * The celebration, and only when there is something to celebrate: 80% or better.
       *
       * A practice run earns it on the same terms as a graded one - they answered the questions either
       * way - and it is the SCORE that decides, not the coins, so a quiz whose score the teacher has
       * hidden still celebrates a good one. The older quiz fires this at any score, including nought;
       * confetti for two out of ten says nothing.
       */
      showAnimation: submitted && (result?.percentage ?? 0) >= 80,
    };
  };

  if (launchError) {
    return (
      <div className="aq-shell aq-centered">
        <p className="aq-error">{launchError}</p>
      </div>
    );
  }

  return (
    <AdaptivePracticeContext.Provider
      value={{
        // Only when something has gone WRONG. The frame's notice stays on screen for as long as it
        // is set, so it is no place for something already said in the instructions - a line about
        // carrying on would sit over the questions for the whole quiz.
        notificationMessage: notice,
        isSafeZone: false,
        isLastQuestion: position === questions.length - 1,
        meter: { percentage: 0, level: '' },
        isLoader: loading,
        isCoinAnimation: false,
        /* Only before the sitting begins. The frame draws its own dialog from this, and the
           instructions this screen shows sit over it: fine at the start, where they are two faces of
           the same moment, but a second dialog behind the instructions a student reopens mid-quiz. */
        isShowStartPopup: showStartPopup && !started,
        // The result dialog gives way to the review once they have read it.
        isShowAdaptivePopup: ask !== 'none' || (submitted && !reviewing),
        isCloseFromClick: false,
        // The drawn question carries its own spoken text; a question shown from its snapshot has
        // none, so the one remembered from when it WAS drawn stands in. Read question then works on
        // every question, not only the one being answered.
        engineObj:
          response?.sound === undefined && sounds[position] !== undefined
            ? { ...response, sound: sounds[position] }
            : response,
        // In a review the renderer marks the question right or wrong; `showAnswer` below is what
        // opens its worked explanation, the same way the teacher's "Show answers" does.
        ...(reviewing
          ? {
              // From the SUBMIT, not from this screen's own memory: a student who reloaded, or who
              // submitted a sitting with nothing left to play, holds no answers locally - every
              // question would otherwise have read as "Not Attempted".
              reviewMode: {
                isAttempted: results.current[questions[position]?.index]?.answered === true,
                isCorrect: results.current[questions[position]?.index]?.isCorrect === true,
                userInput: results.current[questions[position]?.index]?.answer || [],
                correctAnswer: questions[position]?.review_obj?.answerObj,
              },
            }
          : {}),
        options: {
          isMusicAllowed: false,
          isShowExpNext: false,
          /**
           * This attempt is being READ, not answered, so nothing in the question responds - no click, no
           * key, no drag, no focus.
           *
           * `reviewMode` already stops the answer being changed, but only in the part that checks
           * answers: the pieces a question is built from would still have taken a drag or a
           * keystroke, which on someone else's answers is simply wrong. This is the same flag the
           * quiz builder shows its questions with.
           */
          readOnly: storedReview,
          /**
           * What the question is worth, in the coin counter's place - while answering AND while
           * looking the attempt over afterwards. One setting for both, now that it does not live
           * inside questionSummaryHeader, which a review does not have.
           *
           * Not in practice: nothing is earned there, so "worth 5" promises something that will not
           * arrive. And not when the teacher has turned the score off - the server withholds the
           * points then, so there would be nothing to show in any case.
           */
          ...(!practice && showScore && questions[position]?.points !== undefined
            ? { questionPoints: questions[position]?.points }
            : {}),
          // One attempt: the answer is final and the question closes behind it. They can still come
          // back and clear it if they want to answer differently.
          singleAttempt: true,
          /**
           * And nothing is revealed WHILE PLAYING - no hint, no worked answer. A test must not hand
           * a student the answer to a question they have just missed.
           *
           * Said here rather than taken from `singleAttempt`, because they are separate things: one
           * attempt is about how many goes they get, this is about what they are shown. A future
           * mode could want one attempt and the explanation with it.
           *
           * TRUE in the review, where looking the answers over is the whole point: that opens each
           * question's worked explanation rather than leaving it folded behind an arrow, which is
           * what the review is for. Unset, the box still opened - it carries the verdict - but the
           * explanation inside it started collapsed, so the answer the review exists to show was one
           * tap away on every question.
           */
          showAnswer: reviewing,
          /**
           * NO close button while a GRADED quiz is being answered: the way out is to hand it in. A
           * student who really must go closes the tab or goes back, and what they have answered is
           * already saved - see the note on the removed `beforeunload` above.
           *
           * Practice is the opposite case and gets one. Nothing there is saved, scored or spent, and
           * Submit only appears once every question has been opened - so without a close button the
           * only way out of a practice run is to walk through all of it and hand in something that
           * is thrown away. There is nothing to protect, so there is nothing to trap them for.
           *
           * In the review it is there too, because by then there is nothing to lose by leaving.
           */
          isShowClose: reviewing || practice,
          /**
           * NOTHING that describes a question until there is one.
           *
           * These are what the frame draws its chrome from - the clock and tallies strip, the
           * Prev/Submit/Next row, the review's own arrows - and each describes the state of a sitting
           * that does not exist until the first question has loaded. A clock with no time and empty
           * tallies behind the loader say nothing true.
           *
           * The frame treats an absent option as "do not draw it", so withholding them is all that is
           * needed: the screen that owns the state decides when the state is worth describing.
           */
          ...(loading
            ? {}
            : reviewing
            ? {
                // Reading back, not playing: one question at a time with a way forward and back, and
                // none of the things that belong to a sitting - no clock, no tallies, no Submit.
                //
                // A review of a SINGLE question needs no Prev/Next at all: there is nowhere to go,
                // and drawn anyway it is two dead arrows and a "1/1" beside the only question on
                // screen. The frame reads an absent option as "do not draw it", so withholding the
                // row is all this takes.
                ...(questions.length > 1
                  ? {
                      questionSummaryHeader: {
                        current: position,
                        total: questions.length,
                        /**
                         * How the whole attempt went, so the numbers say that and not only where they
                         * are. From the submit's own figures - the same place the mark on the question
                         * itself comes from - so the two cannot disagree.
                         */
                        questionsStatus: questions.map((q) => {
                          const result = results.current[q.index];
                          return result?.answered !== true ? AssessmentQuestionStatus.unanswered
                            : result.isCorrect ? AssessmentQuestionStatus.correct
                              : AssessmentQuestionStatus.wrong;
                        }),
                        /**
                         * Moving in a REVIEW. Not goTo: there is no sitting to charge and nothing to hand
                         * over here. But the screen has to let go of the question it is showing, exactly
                         * as moving does while playing.
                         *
                         * Without that, a question that fails to draw left the PREVIOUS one on screen
                         * under the new number - going from 3 to 2 showed question 3 labelled 2 - and the
                         * message about the failure outlived the question it belonged to. Now the area is
                         * empty and the message belongs to what is actually on screen.
                         */
                        setCurrentPos: (next: number) => {
                          if (next === positionRef.current) return;
                          setNotice('');
                          setLiveAt(null);
                          setResponse({ component: [] });
                          setPosition(next);
                        },
                      },
                    }
                  : {}),
              }
            : {
                questionSummaryHeader: {
                  ...(timeLeft === null
                    ? {}
                    : {
                        timer: {
                          // The frame counts this down and ends the quiz on its own. What this screen does
                          // is revise the figure whenever the server reports less time than was thought.
                          initialTime: timeLeft,
                          // STOPPED once the quiz is in: the countdown kept ticking behind the result
                          // popup, so a quiz already handed in still looked as though it were being
                          // timed. The frame's countdown runs off this flag, so turning it off clears
                          // the interval. Still running while the submit confirmation is open, which is
                          // deliberate - deciding whether to hand in costs the time it costs, and
                          // pausing there would let the clock be held by leaving the dialog open.
                          isStarted: started && !submitted,
                          onTimerEnd: () => void submit(true),
                        },
                      }),
                  questionsStatus: statuses,
                  current: position,
                  total: questions.length,
                  setCurrentPos: goTo,
                  // The strip only knows this sitting's questions; these know the whole quiz.
                  answeredCount: answeredAll(),
                  unansweredCount: Math.max(
                    0,
                    (totalQuestions || questions.length) - answeredAll(),
                  ),
                },
                snapShot: {
                  // Not while this is the question on screen: the live render stays, with the
                  // student's answer in it and the parts disabled behind it. The snapshot is kept
                  // all the same - it is what they get when they come back to it.
                  innerHTML: liveAt === position ? undefined : snapShots[position],
                  saveSnapShot: (innerHTML: string) => {
                    if (innerHTML)
                      setSnapShots((current) => ({
                        ...current,
                        [position]: innerHTML,
                      }));
                  },
                  // Only an answered question has anything to withdraw, and handing the frame this
                  // handler is what puts Clear on screen. So the answer can be changed while it is
                  // still the LIVE render - it used to take stepping to another question and back,
                  // to make a snapshot appear for the control to hang off.
                  clearSnapShot: statuses[position] !== AssessmentQuestionStatus.answered ? undefined : () => {
                    setSnapShots((current) => {
                      const next = { ...current };
                      delete next[position];
                      return next;
                    });
                    // Pressing clear is a student saying they want this question again. So the answer is
                    // withdrawn too: the strip, the screen and the server would otherwise disagree - a
                    // blank question, a green number, and an answer still on the record. The seconds it
                    // has taken them are kept, and the question still counts as seen.
                    const question = questions[positionRef.current];
                    if (!question) return;
                    const held = answers.current[question.index];
                    answers.current[question.index] = {
                      index: question.index,
                      status: 'unanswered',
                      answer: [],
                      isCorrect: false,
                      time_spent: held?.time_spent || 1,
                    };
                    setStatuses((current) =>
                      current.map((status, i) =>
                        i === positionRef.current ? AssessmentQuestionStatus.unanswered : status,
                      ),
                    );
                    inFlight.current = handOver();
                    /**
                     * Draw the question again - ALWAYS, and from nothing.
                     *
                     * Two reasons, and both need the redraw rather than just a re-render.
                     *
                     * The question on screen may not even be this one: a question returned to is
                     * shown from its snapshot and never drawn, so the live render can belong to
                     * wherever the student was last. Without this they would clear one question and
                     * be looking at another.
                     *
                     * And the question they just cleared has to become answerable again. The parts
                     * that take an answer hold their own state - which option was chosen, and
                     * whether the question is closed - and nothing above resets it. Handing the
                     * renderer the same question again does not either: React keeps the state of a
                     * component it recognises in the same place. The empty draw first is what
                     * unmounts it, so the redraw mounts a fresh one.
                     *
                     * This used to work by accident: answering swapped the live question for its
                     * snapshot, which unmounted it, and clearing swapped back and mounted it anew.
                     * The snapshot no longer replaces the question being answered - it made the
                     * question appear to reload - so the unmount has to be asked for.
                     */
                    setLiveAt(null);
                    setResponse({ component: [] });
                    void showQuestion();
                  },
                },
                // Answers are kept until the student saves them - the quiz alone, see the option.
                saveAnswersByHand: true,
                onSubmit: () => setAsk('submit'),
                // The instructions again, for a student who wants to check what they were told. Not
                // through state: see ReopenedInstructions for what redrawing the question would cost.
                onShowInstructions: () => openInstructions.current?.(true),
                // A replay is finished, not handed in: nothing is sent and nothing is marked.
                submitLabel: practice ? 'Finish' : 'Submit',
                submitTooltip: practice ? 'Finish your practice' : 'Submit your responses',
              }),
        },
        loadContent: () => void showQuestion(),
        saveQuestionResponse: async (answerObj: any) => onAnswer(answerObj),
        getPopUpInfo: popUpInfo,
        /**
         * Two different controls call this, and they mean different things.
         *
         * The frame's close button passes `true`: that is a student LEAVING, which a quiz allows -
         * their answers are already with the server, the questions they have seen will not come back,
         * and the clock carries on. So the sitting is handed over and the screen closes. The
         * assessment hides its close button because a Rise test must be finished in one go; a quiz
         * must not trap anybody, least of all inside a full-screen frame with no way out.
         *
         * The dialog's own "Keep Answering" passes its click event instead, which is how the two are
         * told apart - it simply closes the dialog.
         */
        setShowAdaptivePopup: (isCloseFromClick?: boolean) => {
          /**
           * An attempt opened to be read: close means close.
           *
           * Said first and on its own, because none of the branches below fit. This screen is
           * `reviewing` but was never `submitted` - nothing was handed in here - and it is not
           * `practice`, so the click fell through to `setAsk('none')` and the frame stayed open.
           * There is nothing to hand in, nothing to warn about and nothing to go back to.
           */
          if (storedReviewRef.current) {
            state?.messageForParent?.('close');
            return;
          }
          if (submitted) {
            // After finishing, this is "Review Answers" - the way into the review. Once in it, the
            // way out.
            if (!reviewing) {
              void startReview();
              return;
            }
            state?.messageForParent?.('close');
            return;
          }
          /**
           * The frame's close button, mid-practice: ask before going.
           *
           * Nothing is lost by leaving a practice run - it writes nothing either way - but the click
           * is easy to make by accident in a full-screen frame, and losing a half-finished run to a
           * misplaced tap is annoying however little it cost. So it asks. The graded quiz has no
           * close button at all, so this only ever runs in practice.
           *
           * `isCloseFromClick` is what tells the two callers apart: the frame's close button passes
           * true, while the dialog's Continue button is wired straight to onClick and arrives with
           * an event instead.
           */
          if (practice && isCloseFromClick === true) {
            setAsk('leave');
            return;
          }
          setAsk('none');
        },
        /**
         * Start, or the instructions being put away.
         *
         * The hand-over here is what starts the CLOCK: the server charges a sitting from its first
         * hand-over, not from the moment the screen opened, so the time spent reading the instructions
         * is nobody's to pay for. Without this the first beat would be a minute later and the student
         * would get that minute free.
         */
        setShowStartPopup: (status: boolean) => {
          setShowStartPopup(status);
          if (status === false) {
            /* FULL SCREEN, on the press that starts the sitting.
       
               Here because this is where that press lands: the quiz's start screen is its own
               instructions overlay, and its button calls this. A browser grants full screen only from
               a gesture, so it cannot be asked for when the screen loads - and this is the gesture.
               A test is the one thing here worth the whole window: more of the question, and nothing
               else of the page to wander into. They can leave it whenever they like; the frame has a
               button for that, and Escape works as it always does. */
            if (!started) toggleFullSceen('fullScreen');
            setStarted(true);
          }
          if (status === false && !practice) {
            openedAt.current = new Date();
            inFlight.current = handOver('beat');
          }
        },
        /**
         * The dialog's main button, whatever it currently says.
         *
         * Mid-quiz the dialog is the submit confirmation, so this IS the submit - it is not a second
         * way of opening the dialog, which is what it looked like at first and would have left the
         * button doing nothing. Once submitted it says Okay, and the quiz is over.
         */
        onclose: () => {
          // A stored attempt has nothing to hand in, so whatever reaches this closes the screen.
          // Without it the fall-through at the end would be a submit.
          if (storedReviewRef.current) {
            state?.messageForParent?.('close');
            return;
          }
          // Okay, at every stage: the quiz is done with and the screen closes. Looking back over the
          // answers is the other button's job.
          if (submitted) {
            state?.messageForParent?.('close');
            return;
          }
          // Confirming the close button in practice - so this is Leave, not Submit.
          if (ask === 'leave') {
            state?.messageForParent?.('close');
            return;
          }
          void submit(false);
        },
      }}
    >
      <PlayFrame />
      {!loading && showStartPopup ? (
        <AssessmentInstructions
          title={practice ? 'Practice' : 'Quiz Instructions'}
          points={instructionPoints()}
          startLabel={practice ? 'Start Practice' : 'Start Quiz'}
        />
      ) : null}
      <ReopenedInstructions bind={openInstructions}
        title={practice ? 'Practice' : 'Quiz Instructions'} points={instructionPoints()} />
    </AdaptivePracticeContext.Provider>
  );
}

export default AdaptiveQuizPlayer;
